import { computed, ref, watch, type VNode, reactive } from "vue";
import {
  autoResetRef,
  refAutoReset,
  useColorMode,
  useWindowSize,
} from "@vueuse/core";
import { defineStore, storeToRefs } from "pinia";
import { usePersistStore } from "@shared/store/persist";
import { CONFIG } from "@shared/config";
import type {
  NavigationItem,
  UserLocation,
  WLRelease,
} from "@shared/store/ui/types";
import { Addons, Products } from "@shared/types/config";

import type { ExchangeRatesResponse } from "@shared/composables/conversion/use-currency/types";
import { useRoute, useRouter } from "vue-router";
import { fetchUserLocation as _fetchUserLocation } from "@shared/store/ui/helpers";
import logger from "@shared/utils/logger";
import type { CountryCodeAlpha2 } from "@shared/generated/graphql/chargetrip";
import type { Modes } from "@chargetrip/javascript-sdk";
import { RouteName } from "@shared/types/routes";

const {
  application: {
    products,
    addons,
    initialization: {
      ui: {
        themes: { defaultMode },
        onboarding,
      },
      stationDatabasesPerCountry,
    },
  },
  features: {
    settings: {
      primary: { whatsNew },
    },
  },
} = CONFIG;

export const useUIStore = defineStore("ui", () => {
  const isMobileSearchHidden = ref(false);
  // NOTE: Disable route transitions when going from a full screen page to
  // another. That is, going from `/plan-route` to `/range-spider` or vice versa.
  const primaryBottomSheetProps = reactive({
    snapIndex: 0,
    isInteractive: false,
  });
  const bottomSheetTarget = ref<string | null>(null);
  const searchSnapIndex = ref(0);
  const isPrimaryPanelRouteTransitionDisabled = ref(false);
  const hasPrimaryPanelScroll = ref(true);
  const hasPrimaryPanelExpandableAnimation = autoResetRef(false, 500);
  const isResizingWindow = refAutoReset(false, 100);
  const hasTouch = ref(false);
  const isSearchModalVisible = ref(false);
  const bottomSheetContainer = ref<HTMLElement>();
  const releaseList = ref<WLRelease[]>([]);
  const currencyRates = ref<ExchangeRatesResponse | null>(null);
  const persistStore = usePersistStore();
  const userLocation = ref<UserLocation>();
  const navigation = ref<NavigationItem[]>([]);
  const bottomSheetOpenFraction = ref(0);
  // Can't rely on router.query because you need to wait for router.isReady(), which causes a bunch of problems.
  const countryDatabaseByQuery = ref<CountryCodeAlpha2 | null>(
    new URL(window.location.href).searchParams.get(
      "dbCountry",
    ) as CountryCodeAlpha2,
  );

  let userLocationPromise: Promise<UserLocation>;

  const route = useRoute();
  const router = useRouter();

  const countryDatabase = computed<CountryCodeAlpha2 | null>(() => {
    if (!stationDatabasesPerCountry) return null;

    if (
      countryDatabaseByQuery.value &&
      countryDatabaseByQuery.value in stationDatabasesPerCountry
    ) {
      return countryDatabaseByQuery.value;
    }

    if (
      userLocation.value?.country &&
      userLocation.value.country in stationDatabasesPerCountry
    ) {
      return userLocation.value.country;
    }

    return null;
  });

  const isPrimaryView = computed(() => {
    return [
      RouteName.SEARCH,
      RouteName.PLAN_ROUTE,
      RouteName.PLAN_TRUCK_ROUTE,
      RouteName.RANGE_SPIDER,
    ].includes(route.name as RouteName);
  });

  const { stop } = watch(
    [countryDatabase, route],
    ([dbCountry]) => {
      if (!dbCountry || !route.name) return;

      router.replace({
        path: route.path,
        query: { ...route.query, dbCountry },
      });
      stop();
    },
    { immediate: true },
  );

  const { system: systemTheme, store: themeStore } = useColorMode<Modes>({
    attribute: "data-theme",
    initialValue: defaultMode,
  });

  const mode = computed<Modes>(() => {
    if (themeStore.value === "auto") {
      return systemTheme.value === "light" ? "light" : "dark";
    }

    return themeStore.value;
  });

  const configThemeStyle = computed(() => {
    const {
      map: { style },
    } = CONFIG;

    return mode.value === "light" ? style.light : style.dark;
  });

  const fetchUserLocation = async (): Promise<UserLocation> => {
    if (userLocationPromise) {
      return userLocationPromise;
    }

    userLocationPromise = _fetchUserLocation();

    userLocation.value = await userLocationPromise;
    return userLocation.value;
  };

  const { persist } = storeToRefs(persistStore);

  watch(
    () => persistStore.persist.productMode,
    (productMode) =>
      document.documentElement.setAttribute(
        "data-product",
        productMode.toLowerCase(),
      ),
    { immediate: true },
  );

  const { width, height } = useWindowSize();

  watch([width, height], () => {
    isResizingWindow.value = true;
  });

  const isVehiclesModalVisible = computed(() => {
    return (
      typeof route?.query?.modal === "string" &&
      route?.query?.modal?.startsWith("vehicles")
    );
  });

  const isMoreModalVisible = computed(() => {
    return (
      typeof route?.query?.modal === "string" &&
      route?.query?.modal?.startsWith("more")
    );
  });

  const isAdditionalVehiclesModalVisible = computed(() => {
    return (
      typeof route?.query?.modal === "string" &&
      route?.query?.modal?.startsWith("additional-vehicles")
    );
  });

  const isNavigationEnabled = computed<Record<NavigationItem, boolean>>(() => {
    const hasNavigation = navigation.value.length;

    const hasNavigationSearch =
      !hasNavigation || navigation.value.includes("search");

    const hasNavigationRouting =
      !hasNavigation || navigation.value.includes("routing");

    const hasNavigationRange =
      !hasNavigation || navigation.value.includes("range");

    return {
      search: hasNavigationSearch,
      routing: hasNavigationRouting,
      range: hasNavigationRange,
    };
  });

  const isProductEnabled = computed<Record<Products | Addons, boolean>>(() => {
    return {
      stations: products.includes(Products.STATIONS),
      routing: products.includes(Products.ROUTING),
      range: products.includes(Products.RANGE),
      search: addons.includes(Addons.SEARCH),
      climateReporting: addons.includes(Addons.CLIMATE_REPORTING),
      settings: addons.includes(Addons.SETTINGS),
    };
  });

  const isOnboardingComplete = computed(() => {
    const onboardingItems = [
      {
        isComplete: persist.value.completedOnboardingSteps.slider,
        isHidden: onboarding.slider.isHidden,
      },
      {
        isComplete: persist.value.completedOnboardingSteps.vehicles,
        isHidden: onboarding.vehicles.isHidden,
      },
      {
        isComplete: persist.value.completedOnboardingSteps.cookies,
        isHidden: onboarding.cookies.isHidden,
      },
      {
        isComplete: persist.value.completedOnboardingSteps.welcome,
        isHidden: onboarding.welcome.isHidden,
      },
    ];

    // Regardless of which items are enabled, there should be zero (`0`) items
    // left if we filter for those that are a) not hidden, and b) have not been
    // completed.
    return (
      onboardingItems.filter(
        ({ isComplete, isHidden }) => !isHidden && !isComplete,
      ).length === 0
    );
  });

  const isLastReleaseUnseen = computed(() => {
    const lastRelease = releaseList.value[0];
    if (!lastRelease) {
      return false;
    }

    return persist.value.lastSeenRelease !== lastRelease.date;
  });

  const showWhatsNewModal = computed(() => {
    if (whatsNew?.isHidden) {
      return false;
    }

    return isLastReleaseUnseen.value;
  });

  const fetchReleases = async (): Promise<void> => {
    try {
      releaseList.value = (await fetch("/cached/release-log/data.json").then(
        (res) => res.json(),
      )) as Array<WLRelease>;
    } catch {
      releaseList.value = [];
    }
  };

  fetchReleases();

  const fetchCurrencyRates = async (): Promise<void> => {
    try {
      const response = await fetch("/cached/currency-rates/data.json");

      currencyRates.value = await response.json();
    } catch {
      throw new Error("could not fetch currency exchange rate");
    }
  };

  fetchCurrencyRates().catch((err: Error) =>
    logger.warn(`Could not fetch currency rates`, err),
  );

  const infoOverlay = ref<{
    isVisible: boolean;
    title: string;
    content?: () => VNode;
  }>({
    isVisible: false,
    title: "",
    content: undefined,
  });

  const setInfoOverlay = (
    isVisible: boolean,
    title: string,
    content?: () => VNode,
  ): void => {
    infoOverlay.value = {
      isVisible,
      title,
      content,
    };
  };

  return {
    windowWidth: width,
    windowHeight: height,
    userLocation,
    navigation,
    fetchUserLocation,
    isPrimaryView,
    isVehiclesModalVisible,
    isMoreModalVisible,
    isAdditionalVehiclesModalVisible,
    isResizingWindow,
    isSearchModalVisible,
    isLastReleaseUnseen,
    isMobileSearchHidden,
    isNavigationEnabled,
    isOnboardingComplete,
    isPrimaryPanelRouteTransitionDisabled,
    hasPrimaryPanelScroll,
    hasPrimaryPanelExpandableAnimation,
    hasTouch,
    releaseList,
    currencyRates,
    showWhatsNewModal,
    bottomSheetContainer,
    mode,
    infoOverlay,
    primaryBottomSheetProps,
    bottomSheetTarget,
    searchSnapIndex,
    isProductEnabled,
    countryDatabase,
    setInfoOverlay,
    bottomSheetOpenFraction,
    configThemeStyle,
    systemTheme,
    themeStore,
  };
});
